<template>
    <Header />
    <main class="main">
        <div class="page-heading">
            <div>
                <h1>Playlists du lobby</h1>
                <p>Gérez les titres et les playlists YouTube diffusées sur vos écrans.</p>
            </div>
            <button class="primary-button" :disabled="pending" @click="openAdd">Ajouter une playlist</button>
        </div>
        <p v-if="notice" class="success" role="status">{{ notice }}</p>
        <p v-if="loading" role="status">Chargement des playlists…</p>
        <div v-else-if="loadError" class="error" role="alert">
            <p>{{ loadError }}</p><button class="secondary-button" @click="loadPlaylists">Réessayer</button>
        </div>
        <template v-else>
            <div v-if="!playlists.length" class="empty">
                <h2>Aucune playlist pour le moment</h2>
                <p>Ajoutez votre première playlist avec le bouton ci-dessus.</p>
            </div>
            <div v-else class="table-wrapper">
                <table>
                    <caption>{{ playlists.length }} playlist{{ playlists.length > 1 ? 's' : '' }}</caption>
                    <thead>
                        <tr>
                            <th scope="col">Titre</th>
                            <th scope="col">URL YouTube</th>
                            <th scope="col">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="item in playlists" :key="item.id">
                            <template v-if="editingId === item.id">
                                <td><input v-model="editTitle" :aria-label="`Titre de ${item.title}`" maxlength="100"
                                        :disabled="pending" @keydown.enter.prevent="saveEdit"
                                        @keydown.esc="!pending && (editingId = null)"></td>
                                <td><input v-model="editUrl" :aria-label="`URL de ${item.title}`" :disabled="pending"
                                        @keydown.enter.prevent="saveEdit" @keydown.esc="!pending && (editingId = null)">
                                    <p class="hint">URL YouTube ou identifiant de playlist.</p>
                                    <p class="hint">Le titre identifie l’écran : vérifiez sa correspondance si vous le
                                        renommez.</p>
                                    <p v-if="editError" class="error" role="alert">{{ editError }}</p>
                                </td>
                                <td>
                                    <div class="actions"><button class="success-button" :disabled="pending"
                                            @click="saveEdit">{{ pending ? 'Enregistrement…' : 'Enregistrer'
                                            }}</button><button class="secondary-button-outlined" :disabled="pending"
                                            @click="editingId = null">Annuler</button></div>
                                </td>
                            </template>
                            <template v-else>
                                <th scope="row">{{ item.title }}</th>
                                <td><a :href="playlistUrl(item.playlist)" target="_blank" rel="noopener noreferrer">{{
                                    playlistUrl(item.playlist) }}</a></td>
                                <td>
                                    <div class="actions"><button class="primary-button-outlined"
                                            :disabled="pending || editingId !== null"
                                            :aria-label="`Modifier ${item.title}`"
                                            @click="startEdit(item)">Modifier</button><button
                                            class="danger-button-outlined" :disabled="pending || editingId !== null"
                                            :aria-label="`Supprimer ${item.title}`"
                                            @click="openDelete(item)">Supprimer</button></div>
                                </td>
                            </template>
                        </tr>
                    </tbody>
                </table>
            </div>
        </template>
    </main>

    <Modal v-model="addModal">
        <form class="playlist-form" aria-labelledby="add-title" @submit.prevent="addPlaylist">
            <h2 id="add-title">Ajouter une playlist</h2>
            <p class="intro">Associez un titre à une playlist YouTube.</p>
            <label for="playlist-title">Titre</label><input id="playlist-title" v-model="title" maxlength="100" required
                :disabled="pending" placeholder="Ex. : Hall principal">
            <label for="playlist-url">URL ou identifiant de playlist</label><input id="playlist-url" v-model="playlist"
                required :disabled="pending" placeholder="https://www.youtube.com/playlist?list=…"
                aria-describedby="playlist-help">
            <p id="playlist-help" class="hint">Copiez le lien de la playlist, ou son identifiant après « list= ». Le
                titre
                doit correspondre au nom de l’écran.</p>
            <p v-if="addError" class="error" role="alert">{{ addError }}</p>
            <div class="form-actions"><button type="button" class="secondary-button-outlined" :disabled="pending"
                    @click="addOpen = false">Annuler</button><button class="primary-button" :disabled="pending">{{
                        pending ?
                            'Ajout…' : 'Ajouter la playlist' }}</button></div>
        </form>
    </Modal>
    <Modal v-model="deleteModal">
        <div class="playlist-form" aria-labelledby="delete-title">
            <h2 id="delete-title">Supprimer cette playlist ?</h2>
            <p><strong>{{ deleteTarget?.title }}</strong> sera supprimée de la configuration du lobby. La playlist
                restera
                disponible sur YouTube.</p>
            <p v-if="deleteError" class="error" role="alert">{{ deleteError }}</p>
            <div class="form-actions"><button class="secondary-button-outlined" :disabled="pending"
                    @click="deleteTarget = null">Annuler</button><button class="danger-button" :disabled="pending"
                    @click="deletePlaylist">{{ pending ? 'Suppression…' : 'Supprimer la playlist' }}</button></div>
        </div>
    </Modal>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue'
import apiClient from '@/api'
import Header from '@/components/Header.vue'
import Modal from '@/components/Modal.vue'

const playlists = ref([])
const loading = ref(true)
const loadError = ref('')
const notice = ref('')
const editingId = ref(null)
const editTitle = ref('')
const editUrl = ref('')
const editError = ref('')
const addOpen = ref(false)
const title = ref('')
const playlist = ref('')
const addError = ref('')
const deleteTarget = ref(null)
const deleteError = ref('')
const pending = ref(false)
const addModal = computed({ get: () => addOpen.value, set: value => { if (!pending.value) addOpen.value = value } })
const deleteModal = computed({ get: () => !!deleteTarget.value, set: value => { if (!value && !pending.value) deleteTarget.value = null } })
const playlistUrl = id => `https://www.youtube.com/playlist?list=${encodeURIComponent(id)}`

const errorMessage = error => {
    const data = error.response?.data
    if (typeof data?.message === 'string') return data.message
    if (typeof data?.detail === 'string') return data.detail
    if (data && typeof data === 'object') return Object.values(data).flat().join(' ')
    return 'La demande a échoué. Veuillez réessayer.'
}
const extractPlaylistId = input => {
    const value = input.trim()
    if (/^[a-zA-Z0-9_-]{10,200}$/.test(value)) return value
    try {
        const url = new URL(value)
        if (!['http:', 'https:'].includes(url.protocol) || !['youtube.com', 'www.youtube.com', 'm.youtube.com', 'music.youtube.com', 'youtu.be', 'www.youtu.be'].includes(url.hostname)) return null
        const id = url.searchParams.get('list') || ''
        return /^[a-zA-Z0-9_-]{10,200}$/.test(id) ? id : null
    } catch { return null }
}
const loadPlaylists = async () => {
    loading.value = true
    loadError.value = ''
    try {
        const { data } = await apiClient.get('playlists/')
        playlists.value = data
    } catch (error) { loadError.value = errorMessage(error) }
    finally { loading.value = false }
}
const openAdd = () => {
    title.value = ''
    playlist.value = ''
    addError.value = ''
    notice.value = ''
    addOpen.value = true
}
const startEdit = item => {
    editingId.value = item.id
    editTitle.value = item.title
    editUrl.value = playlistUrl(item.playlist)
    editError.value = ''
    notice.value = ''
}
const saveEdit = async () => {
    if (pending.value) return
    editError.value = ''
    const id = extractPlaylistId(editUrl.value)
    if (!editTitle.value.trim() || !id) {
        editError.value = 'Saisissez un titre et une URL YouTube contenant une playlist, ou son identifiant.'
        return
    }
    pending.value = true
    try {
        const { data } = await apiClient.patch(`playlists/${editingId.value}/`, { title: editTitle.value.trim(), playlist: id })
        playlists.value = playlists.value.map(item => item.id === data.id ? data : item)
        editingId.value = null
        notice.value = 'La playlist a été mise à jour.'
    } catch (error) { editError.value = errorMessage(error) }
    finally { pending.value = false }
}
const addPlaylist = async () => {
    if (pending.value) return
    addError.value = ''
    const id = extractPlaylistId(playlist.value)
    if (!title.value.trim() || !id) {
        addError.value = 'Saisissez un titre et une URL YouTube contenant une playlist, ou son identifiant.'
        return
    }
    pending.value = true
    try {
        const { data } = await apiClient.post('add_playlist/', { title: title.value.trim(), playlist: id })
        playlists.value.push(data.data)
        addOpen.value = false
        notice.value = 'La playlist a été ajoutée.'
    } catch (error) { addError.value = errorMessage(error) }
    finally { pending.value = false }
}
const openDelete = item => {
    deleteTarget.value = item
    deleteError.value = ''
    notice.value = ''
}
const deletePlaylist = async () => {
    if (pending.value) return
    pending.value = true
    deleteError.value = ''
    const id = deleteTarget.value.id
    try {
        await apiClient.delete(`playlists/${id}/`)
        playlists.value = playlists.value.filter(item => item.id !== id)
        if (editingId.value === id) editingId.value = null
        deleteTarget.value = null
        notice.value = 'La playlist a été supprimée.'
    } catch (error) { deleteError.value = errorMessage(error) }
    finally { pending.value = false }
}
onMounted(loadPlaylists)
</script>

<style scoped lang="scss">
.main {
    max-width: 1200px;
    margin: 2rem auto;
    padding: 2rem;
    background: white;
    border-radius: 15px;
}

.page-heading {
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 1rem;
    margin-bottom: 1.5rem;

    h1 {
        color: $dark-blue;
        font-size: 1.7rem;
        margin: 0 0 .5rem;
    }

    p {
        margin: 0;
        color: #646d7b;
    }
}

.table-wrapper {
    overflow-x: auto;
}

table {
    width: 100%;
    table-layout: fixed;
    border: none;
    border-collapse: separate;
    border-spacing: 0;

    caption {
        text-align: left;
        color: #646d7b;
        font-size: .85rem;
        margin-bottom: .75rem;
    }
}

th,
td {
    border: none;
    text-align: left;
    overflow-wrap: anywhere;
}

thead th {
    background: #f4f7fb;
    color: $dark-blue;
    font-size: .75rem;
    font-weight: 700;
    letter-spacing: .06em;
    text-transform: uppercase;
    padding: .8rem 1rem;
    border-bottom: 2px solid #e3e9f2;

    &:first-child {
        width: 22%;
        border-top-left-radius: 10px;
    }

    &:last-child {
        width: 28%;
        border-top-right-radius: 10px;
    }
}

tbody td,
tbody th {
    background: white;
    padding: .85rem 1rem;
    border-bottom: 1px solid #eef1f6;
    vertical-align: middle;
    transition: background-color .15s;
}

tbody th {
    font-weight: 600;
    color: $dark-blue;
}

tbody tr:last-child > :is(th, td) {
    border-bottom: none;
}

tbody tr:hover > :is(th, td) {
    background: #f8fafd;
}

@media (prefers-reduced-motion: reduce) {
    tbody td,
    tbody th {
        transition: none;
    }
}

a {
    color: $dark-blue;
    font-size: .9rem;
}

.actions,
.form-actions {
    display: flex;
    gap: .5rem;
    flex-wrap: wrap;
}

.actions button {
    font-size: .85rem;
    padding: .5rem .75rem;
}

input {
    width: 100%;
    min-width: 0;
    box-sizing: border-box;
    font-family: inherit;
    font-size: .95rem;
    padding: .6rem .9rem;
    border: 1px solid #dbdee3;
    border-radius: 8px;
    outline-color: $dark-blue;
}

.playlist-form {
    width: min(460px, calc(100vw - 8rem));
    padding-block: 1rem;

    h2 {
        color: $dark-blue;
        margin-top: 0;
        padding-right: 1rem;
    }

    p {
        line-height: 1.6;
    }

    label {
        display: block;
        font-weight: 600;
        margin: 1.25rem 0 .5rem;
    }
}

.intro {
    color: #646d7b;
}

.form-actions {
    justify-content: flex-end;
    margin-top: 1.75rem;
}

.hint {
    color: #646d7b;
    font-size: .8rem;
    line-height: 1.5;
    margin-bottom: 0;
}

.error {
    color: #a62323;
    font-size: .9rem;
}

.success {
    color: #246747;
    padding: .75rem 1rem;
    background: #e7f6ee;
    border-radius: 8px;
}

.empty {
    padding: 2.5rem 1rem;
    text-align: center;
    border: 1px dashed #cdd4df;
    border-radius: 10px;

    h2 {
        font-size: 1.2rem;
        color: $dark-blue;
    }
}

button:disabled {
    opacity: .55;
    cursor: not-allowed;
}

:is(button, a):focus-visible {
    outline: 3px solid $dark-blue;
    outline-offset: 3px;
}

@media (max-width: 760px) {
    .main {
        margin: 1rem;
        padding: 1rem;
    }

    table {
        min-width: 700px;
    }

    .form-actions {
        flex-direction: column-reverse;
    }
}
</style>
